import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Clock, CreditCard, Star, MapPin, Shield } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { TestimonialsMarquee } from './TestimonialsMarquee';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { toast } from '../hooks/use-toast';

const LandingPage = () => {
  const navigate = useNavigate();
  const { canInstall, installed, promptInstall } = usePwaInstall();

  const handleDownloadApp = async () => {
    if (installed) {
      toast({ title: 'Already installed', description: 'BookMyDriver is already on your home screen' });
      return;
    }
    const outcome = await promptInstall();
    if (outcome === 'unavailable') {
      toast({
        title: 'Install from your browser menu',
        description: 'Open your browser menu and choose "Add to Home Screen" or "Install App" to add BookMyDriver.',
      });
    } else if (outcome === 'accepted') {
      toast({ title: 'Installing…', description: 'BookMyDriver is being added to your home screen' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-card to-muted">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 flex justify-between items-center gap-2">
          <BrandLogo variant="compact" className="h-8 sm:h-10 md:h-12 w-auto flex-shrink-0" data-testid="brand-logo-header" />
          <div className="flex gap-1.5 sm:gap-3 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/driver/login')}
              className="hover:bg-muted transition-colors px-2.5 sm:px-4 text-xs sm:text-sm whitespace-nowrap"
            >
              Driver Login
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadApp}
              className="bg-primary hover:bg-primary-dark transition-colors px-2.5 sm:px-4 text-xs sm:text-sm whitespace-nowrap"
              data-testid="install-app-btn"
            >
              Install App
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold leading-tight">
              Hire Professional Drivers
              <span className="block bg-gradient-to-r from-primary to-accent-dark bg-clip-text text-transparent">
                for Your Car
              </span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Book experienced drivers to drive your personal car on hourly basis. Perfect for city tours, shopping trips, or business meetings.
            </p>
            <div className="flex gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/login?type=user')}
                className="bg-primary hover:bg-primary-dark text-lg px-8 transition-all hover:scale-105"
              >
                Book Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/driver/register')}
                className="text-lg px-8 transition-all hover:scale-105"
              >
                Become a Driver
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="bg-gradient-to-br from-muted to-background rounded-3xl p-8 md:p-12 shadow-2xl flex items-center justify-center">
              <BrandLogo
                variant="full"
                className="w-full max-w-md h-auto drop-shadow-xl"
                data-testid="brand-logo-hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Simple Hourly Pricing</h2>
            <p className="text-xl text-muted-foreground">No hidden charges, transparent rates</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { hours: 1, price: 250 },
              { hours: 2, price: 350 },
              { hours: 3, price: 400 },
              { hours: 4, price: 500 },
              { hours: 5, price: 600 },
              { hours: 6, price: 700 },
              { hours: 7, price: 800 },
              { hours: 8, price: 900 },
            ].map((plan) => (
              <Card key={plan.hours} className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2">
                <Clock className="w-12 h-12 mx-auto mb-4 text-foreground" />
                <h3 className="text-3xl font-bold mb-2">{plan.hours}hr</h3>
                <p className="text-4xl font-bold text-foreground mb-4">₹{plan.price}</p>
                <Button
                  className="w-full bg-primary-dark hover:bg-primary"
                  onClick={() => navigate('/login?type=user')}
                >
                  Select
                </Button>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            +₹200 late night charge applies for pickups between 10 PM and 6 AM
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Us</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 text-center hover:shadow-xl transition-all">
              <Star className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
              <h3 className="text-2xl font-bold mb-3">Verified Drivers</h3>
              <p className="text-muted-foreground">All drivers are background verified and highly rated</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-all">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-primary" />
              <h3 className="text-2xl font-bold mb-3">Real-time Tracking</h3>
              <p className="text-muted-foreground">Track your driver's location in real-time</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-all">
              <Shield className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h3 className="text-2xl font-bold mb-3">Safe & Secure</h3>
              <p className="text-muted-foreground">Your safety is our top priority</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsMarquee />

      {/* FASTag Section */}
      <section className="bg-gradient-to-r from-primary-dark to-primary text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <CreditCard className="w-20 h-20 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">FASTag Recharge</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Recharge your FASTag instantly through our app. Quick, easy, and secure.
          </p>
          <Button
            size="lg"
            className="bg-white text-foreground hover:bg-muted text-lg px-8"
            onClick={() => navigate('/login?type=user')}
          >
            Recharge Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary-dark text-white py-8">
        <div className="container mx-auto px-4 text-center space-y-3">
          <div className="flex items-center justify-center gap-6 text-sm text-white/80">
            <button onClick={() => navigate('/about')} className="hover:text-white transition-colors" data-testid="footer-about-link">About Us</button>
            <button onClick={() => navigate('/contact')} className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact Us</button>
          </div>
          <p className="text-white/70">© 2026 V8 Technologies Private Limited | BookMyDriver.in | All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
